/**
 * Zustand Task Store Implementation - PERFORMANCE OPTIMIZED
 *
 * Uses mutable updates with revision counters to avoid copying 20K+ task objects.
 * Zustand will still trigger re-renders via revision number changes.
 */

import { create, StoreApi, UseBoundStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ITaskStore } from '../ITaskStore';
import type {
  TaskSnapshot,
  LinkSnapshot,
  DhtmlxTask,
  JsonPatch,
  LocationHierarchyPayload,
  StoreListener,
  PatchListener,
} from '../types';
import {
  loadTasksFromDB,
  saveTasksToDB,
  loadLocationTreeFromDB,
  putTasksInDB,
} from '../db';
import { resolveTaskName } from '../../utils/utils';
import type {
  AddNewTaskRequest,
  CreateNewProjectRequest,
  LocationTreeNode,
} from '../../api/schedulingApis';
import {
  getDhtmlxParentId,
  isPlanningShellTask,
} from '../../gantt/addTaskUtils';
import { buildDhtmlxScheduleFields } from '../../gantt/mspInclusiveEndBridge';

/** Flat `sr_no` from the planning API (ROW_NUMBER over the outline path). `0` is valid. */
function flatSrNoRank(task: TaskSnapshot): number | null {
  const raw = task.srNo != null ? String(task.srNo).trim() : '';
  if (!/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function compareTaskSnapshots(a: TaskSnapshot, b: TaskSnapshot): number {
  const aSr = flatSrNoRank(a);
  const bSr = flatSrNoRank(b);
  if (aSr != null && bSr != null && aSr !== bSr) return aSr - bSr;
  if (aSr != null && bSr == null) return -1;
  if (aSr == null && bSr != null) return 1;

  const aOutline = Number(a.outlinePosition ?? Number.MAX_SAFE_INTEGER);
  const bOutline = Number(b.outlinePosition ?? Number.MAX_SAFE_INTEGER);
  if (aOutline !== bOutline) return aOutline - bOutline;

  const aWbs = String(a.wbs ?? '').trim();
  const bWbs = String(b.wbs ?? '').trim();
  if (aWbs && bWbs && aWbs !== bWbs) {
    return aWbs.localeCompare(bWbs, undefined, { numeric: true, sensitivity: 'base' });
  }
  if (aWbs && !bWbs) return -1;
  if (!aWbs && bWbs) return 1;

  const aName = String(a.taskName ?? '').trim();
  const bName = String(b.taskName ?? '').trim();
  if (aName !== bName) {
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  }
  return String(a.id).localeCompare(String(b.id), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Internal Zustand store state
 */
interface TaskStoreState {
  // Data (MUTABLE for performance)
  tasks: Record<string, TaskSnapshot>;
  links: Record<string, LinkSnapshot>;
  // Revision counters (change these to trigger updates without copying data)
  tasksRevision: number;
  linksRevision: number;
  // UI State
  selectedTaskId: string | null;
  autoSchedulingEnabled: boolean;
  loading: boolean;
  error: string | null;
  // Project context
  projectId: string;
  // Session-level change tracking (for delta sync to backend)
  sessionAffectedTaskIds: Set<string>;
  /** User-edited task IDs for UI highlight (subset of / parallel to sessionAffectedTaskIds). */
  sessionEditedTaskIds: Set<string>;
  /** Per-task edited Gantt column names for bold cell styling. */
  sessionEditedTaskFields: Map<string, Set<string>>;
  sessionAffectedRemovedLinkIds: Set<string | number>;
  sessionAffectedAddedLinkIds: Set<string | number>;
  /** Pending dependency POSTs keyed by local/gantt link id. */
  sessionPendingDependencyCreates: Record<string, LinkSnapshot>;
  /** Pending dependency DELETEs (server dependency ids). */
  sessionPendingDependencyDeletes: Set<string>;
  sessionPendingCreates: Record<string, AddNewTaskRequest>;
  sessionPendingDeletes: Set<string>;
  sessionPendingProjectCreate: CreateNewProjectRequest | null;
  sessionStartTime: number | null;
  lastChangeTime: number | null;
  // Location hierarchy (for grid display: locationId → name)
  locationTree: LocationTreeNode[] | null;
}

/**
 * Internal Zustand store actions
 */
interface TaskStoreActions {
  setTasks: (tasks: TaskSnapshot[]) => void;
  setLinks: (links: LinkSnapshot[]) => void;
  updateTask: (id: string, changes: Partial<TaskSnapshot>) => void;
  batchUpdateTasks: (updates: Map<string, Partial<TaskSnapshot>>) => void;
  addTask: (task: TaskSnapshot) => void;
  addTasks: (tasks: TaskSnapshot[]) => void;
  removeTask: (id: string) => void;
  addLink: (link: LinkSnapshot) => void;
  removeLink: (id: number | string) => void;
  setSelectedTaskId: (id: string | null) => void;
  setAutoSchedulingEnabled: (enabled: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setProjectId: (projectId: string) => void;
  // Session change tracking
  addAffectedTasks: (ids: string[]) => void;
  addEditedTasks: (ids: string[], fields?: string[]) => void;
  addAffectedLinks: (ids: Array<number | string>) => void;
  trackAffectedLinkChanges: (
    removed: Array<number | string>,
    added: Array<number | string>,
  ) => void;
  getPendingDependencyCreates: () => LinkSnapshot[];
  getPendingDependencyDeletes: () => string[];
  clearSessionChanges: () => void;
  exportSessionSnapshot: () => import('../../gantt/ganttSessionSnapshot').GanttSessionSnapshot;
  restoreSessionSnapshot: (
    snapshot: import('../../gantt/ganttSessionSnapshot').GanttSessionSnapshot,
  ) => void;
  markPendingCreate: (taskId: string, payload: AddNewTaskRequest) => void;
  unmarkPendingCreate: (taskId: string) => void;
  markPendingDelete: (taskId: string) => void;
  unmarkPendingDelete: (taskId: string) => void;
  setPendingProjectCreate: (payload: CreateNewProjectRequest | null) => void;
  setLocationTree: (tree: LocationTreeNode[] | null) => void;
}

type TaskStore = TaskStoreState & TaskStoreActions;

/**
 * Create the internal Zustand store
 */
function createZustandStore(projectId: string): UseBoundStore<StoreApi<TaskStore>> {
  return create<TaskStore>()(
    devtools(
      (set, get) => ({
    // Initial state
    tasks: {},
    links: {},
    tasksRevision: 0,
    linksRevision: 0,
    selectedTaskId: null,
    autoSchedulingEnabled: false,
    loading: false,
    error: null,
    projectId,
    // Session change tracking
    sessionAffectedTaskIds: new Set<string>(),
    sessionEditedTaskIds: new Set<string>(),
    sessionEditedTaskFields: new Map<string, Set<string>>(),
    sessionAffectedRemovedLinkIds: new Set<number>(),
    sessionAffectedAddedLinkIds: new Set<number>(),
    sessionPendingDependencyCreates: {},
    sessionPendingDependencyDeletes: new Set<string>(),
    sessionPendingCreates: {},
    sessionPendingDeletes: new Set<string>(),
    sessionPendingProjectCreate: null,
    sessionStartTime: null,
    lastChangeTime: null,
    locationTree: null,

    // Actions - MUTABLE updates for performance
    setTasks: (tasks: TaskSnapshot[]) => {
      const newTasks: Record<string, TaskSnapshot> = {};
      for (const task of tasks) {
        newTasks[task.id] = task;
      }

      set({ tasks: newTasks, tasksRevision: get().tasksRevision + 1 }, false, 'setTasks');
    },

    setLinks: (links: LinkSnapshot[]) => {
      const newLinks: Record<string, LinkSnapshot> = {};
      for (const link of links) {
        newLinks[link.id.toString()] = link;
      }

      set({ links: newLinks, linksRevision: get().linksRevision + 1 }, false, 'setLinks');
    },

    updateTask: (id: string, changes: Partial<TaskSnapshot>) => {
      const { tasks } = get();
      const existing = tasks[id];
      if (!existing) return;

      // MUTATE directly (no copy)
      tasks[id] = { ...existing, ...changes };

      // Increment revision to trigger subscribers
      set({ tasksRevision: get().tasksRevision + 1 }, false, `updateTask/${id}`);
    },

    batchUpdateTasks: (updates: Map<string, Partial<TaskSnapshot>>) => {
      const { tasks } = get();

      // MUTATE directly (no copy)
      for (const [id, changes] of updates) {
        const existing = tasks[id];
        if (existing) {
          tasks[id] = { ...existing, ...changes };
        }
      }

      // Increment revision to trigger subscribers
      set({ tasksRevision: get().tasksRevision + 1 }, false, `batchUpdateTasks (${updates.size})`);
    },

    addTask: (task: TaskSnapshot) => {
      get().addTasks([task]);
    },

    addTasks: (incoming: TaskSnapshot[]) => {
      if (incoming.length === 0) return;
      const { tasks, tasksRevision } = get();
      for (const task of incoming) {
        tasks[task.id] = task;
      }
      set({ tasksRevision: tasksRevision + 1 }, false, `addTasks/${incoming.length}`);
    },

    removeTask: (id: string) => {
      const { tasks } = get();

      // MUTATE directly
      delete tasks[id];

      set({ tasksRevision: get().tasksRevision + 1 }, false, `removeTask/${id}`);
    },

    addLink: (link: LinkSnapshot) => {
      const { links } = get();

      // MUTATE directly
      links[link.id.toString()] = link;

      set({ linksRevision: get().linksRevision + 1 }, false, `addLink/${link.id}`);
    },

    removeLink: (id: number | string) => {
      const { links } = get();

      // MUTATE directly
      delete links[id.toString()];

      set({ linksRevision: get().linksRevision + 1 }, false, `removeLink/${id}`);
    },

    setSelectedTaskId: (id: string | null) => {
      set({ selectedTaskId: id }, false, 'setSelectedTaskId');
    },

    setAutoSchedulingEnabled: (enabled: boolean) => {
      set({ autoSchedulingEnabled: enabled }, false, 'setAutoSchedulingEnabled');
    },

    setLoading: (loading: boolean) => {
      set({ loading }, false, 'setLoading');
    },

    setError: (error: string | null) => {
      set({ error }, false, 'setError');
    },

    setProjectId: (projectId: string) => {
      set({ projectId }, false, 'setProjectId');
    },

    // Session change tracking actions
    addAffectedTasks: (ids: string[]) => {
      const { sessionAffectedTaskIds, sessionPendingCreates } = get();

      // Union operation - add all new IDs to the Set (skip pending creates).
      ids.forEach((id) => {
        if (!id) return;
        if (sessionPendingCreates[id] || sessionPendingCreates[String(id)]) return;
        sessionAffectedTaskIds.add(id);
      });

      // Update metadata
      const now = Date.now();
      set({
        sessionStartTime: get().sessionStartTime || now,
        lastChangeTime: now,
      }, false, `addAffectedTasks (${ids.length})`);
    },

    addEditedTasks: (ids: string[], fields?: string[]) => {
      if (ids.length === 0) return;
      const {
        sessionEditedTaskIds,
        sessionAffectedTaskIds,
        sessionEditedTaskFields,
        sessionPendingCreates,
      } = get();
      for (const id of ids) {
        if (!id) continue;
        sessionEditedTaskIds.add(id);
        // Pending creates go through POST (mergeCreatePayloadWithSnapshot), not PUT affected.
        if (!sessionPendingCreates[id] && !sessionPendingCreates[String(id)]) {
          sessionAffectedTaskIds.add(id);
        }
        if (fields && fields.length > 0) {
          let set = sessionEditedTaskFields.get(id);
          if (!set) {
            set = new Set<string>();
            sessionEditedTaskFields.set(id, set);
          }
          for (const f of fields) {
            if (f) set.add(f);
          }
        }
      }
      const now = Date.now();
      set({
        sessionStartTime: get().sessionStartTime || now,
        lastChangeTime: now,
      }, false, `addEditedTasks (${ids.length})`);
    },

    addAffectedLinks: (ids: number[]) => {
      if (ids.length === 0) return;
      const { sessionAffectedAddedLinkIds } = get();
      ids.forEach((id) => sessionAffectedAddedLinkIds.add(id));
      const now = Date.now();
      set({
        sessionStartTime: get().sessionStartTime || now,
        lastChangeTime: now,
      }, false, `addAffectedLinks (${ids.length})`);
    },

    trackAffectedLinkChanges: (removed: number[], added: number[]) => {
      if (removed.length === 0 && added.length === 0) return;
      const {
        links,
        sessionAffectedRemovedLinkIds,
        sessionAffectedAddedLinkIds,
        sessionPendingDependencyCreates,
        sessionPendingDependencyDeletes,
      } = get();
      removed.forEach((id) => sessionAffectedRemovedLinkIds.add(id));
      added.forEach((id) => sessionAffectedAddedLinkIds.add(id));

      const nextCreates = { ...sessionPendingDependencyCreates };
      const nextDeletes = new Set(sessionPendingDependencyDeletes);

      for (const id of added) {
        const key = String(id);
        if (nextDeletes.has(key)) {
          nextDeletes.delete(key);
        } else {
          const snap = links[key] ?? links[id.toString()];
          if (snap) {
            nextCreates[key] = {
              id: snap.id,
              source: String(snap.source),
              target: String(snap.target),
              type: String(snap.type ?? '0'),
              lag: snap.lag ?? null,
            };
          } else {
            console.warn(
              `[TaskStore] Pending dependency create skipped; link ${key} not found in store`,
            );
          }
        }
      }

      for (const id of removed) {
        const key = String(id);
        if (nextCreates[key]) {
          delete nextCreates[key];
        } else {
          nextDeletes.add(key);
        }
      }

      const now = Date.now();
      set({
        sessionPendingDependencyCreates: nextCreates,
        sessionPendingDependencyDeletes: nextDeletes,
        sessionStartTime: get().sessionStartTime || now,
        lastChangeTime: now,
      }, false, `trackAffectedLinkChanges (+${added.length}/-${removed.length})`);
    },

    clearSessionChanges: () => {
      set({
        sessionAffectedTaskIds: new Set<string>(),
        sessionEditedTaskIds: new Set<string>(),
        sessionEditedTaskFields: new Map<string, Set<string>>(),
        sessionAffectedRemovedLinkIds: new Set<number>(),
        sessionAffectedAddedLinkIds: new Set<number>(),
        sessionPendingDependencyCreates: {},
        sessionPendingDependencyDeletes: new Set<string>(),
        sessionPendingCreates: {},
        sessionPendingDeletes: new Set<string>(),
        sessionPendingProjectCreate: null,
        sessionStartTime: null,
        lastChangeTime: null,
      }, false, 'clearSessionChanges');
    },

    exportSessionSnapshot: () => {
      const state = get();
      const editedTaskFields: Record<string, string[]> = {};
      for (const [id, fields] of state.sessionEditedTaskFields) {
        editedTaskFields[id] = Array.from(fields);
      }
      return {
        version: 1 as const,
        affectedTaskIds: Array.from(state.sessionAffectedTaskIds),
        editedTaskIds: Array.from(state.sessionEditedTaskIds),
        editedTaskFields,
        affectedRemovedLinkIds: Array.from(state.sessionAffectedRemovedLinkIds),
        affectedAddedLinkIds: Array.from(state.sessionAffectedAddedLinkIds),
        pendingDependencyCreates: Object.values(state.sessionPendingDependencyCreates),
        pendingDependencyDeletes: Array.from(state.sessionPendingDependencyDeletes),
        pendingCreates: { ...state.sessionPendingCreates },
        pendingDeletes: Array.from(state.sessionPendingDeletes),
        pendingProjectCreate: state.sessionPendingProjectCreate,
        sessionStartTime: state.sessionStartTime,
        lastChangeTime: state.lastChangeTime,
      };
    },

    restoreSessionSnapshot: (snapshot) => {
      if (!snapshot || snapshot.version !== 1) return;
      const editedTaskFields = new Map<string, Set<string>>();
      for (const [id, fields] of Object.entries(snapshot.editedTaskFields ?? {})) {
        editedTaskFields.set(id, new Set(fields));
      }
      const pendingDependencyCreates: Record<string, LinkSnapshot> = {};
      for (const link of snapshot.pendingDependencyCreates ?? []) {
        pendingDependencyCreates[String(link.id)] = link;
      }
      set({
        sessionAffectedTaskIds: new Set(snapshot.affectedTaskIds ?? []),
        sessionEditedTaskIds: new Set(snapshot.editedTaskIds ?? []),
        sessionEditedTaskFields: editedTaskFields,
        sessionAffectedRemovedLinkIds: new Set(snapshot.affectedRemovedLinkIds ?? []),
        sessionAffectedAddedLinkIds: new Set(snapshot.affectedAddedLinkIds ?? []),
        sessionPendingDependencyCreates: pendingDependencyCreates,
        sessionPendingDependencyDeletes: new Set(snapshot.pendingDependencyDeletes ?? []),
        sessionPendingCreates: { ...(snapshot.pendingCreates ?? {}) },
        sessionPendingDeletes: new Set(snapshot.pendingDeletes ?? []),
        sessionPendingProjectCreate: snapshot.pendingProjectCreate ?? null,
        sessionStartTime: snapshot.sessionStartTime ?? null,
        lastChangeTime: snapshot.lastChangeTime ?? null,
      }, false, 'restoreSessionSnapshot');
    },

    setPendingProjectCreate: (payload: CreateNewProjectRequest | null) => {
      const now = Date.now();
      set({
        sessionPendingProjectCreate: payload,
        sessionStartTime: get().sessionStartTime || (payload ? now : get().sessionStartTime),
        lastChangeTime: payload ? now : get().lastChangeTime,
      }, false, payload ? 'setPendingProjectCreate' : 'clearPendingProjectCreate');
    },

    markPendingCreate: (taskId: string, payload: AddNewTaskRequest) => {
      const state = get();
      const nextDeletes = new Set(state.sessionPendingDeletes);
      nextDeletes.delete(taskId);
      state.sessionEditedTaskIds.add(taskId);
      let fields = state.sessionEditedTaskFields.get(taskId);
      if (!fields) {
        fields = new Set<string>();
        state.sessionEditedTaskFields.set(taskId, fields);
      }
      for (const f of ['text', 'start_date', 'end_date', 'duration'] as const) {
        fields.add(f);
      }
      const now = Date.now();
      set({
        sessionPendingCreates: { ...state.sessionPendingCreates, [taskId]: payload },
        sessionPendingDeletes: nextDeletes,
        sessionStartTime: state.sessionStartTime || now,
        lastChangeTime: now,
      }, false, `markPendingCreate/${taskId}`);
    },

    unmarkPendingCreate: (taskId: string) => {
      const { sessionPendingCreates, sessionEditedTaskIds, sessionEditedTaskFields } = get();
      if (!sessionPendingCreates[taskId]) return;
      const next = { ...sessionPendingCreates };
      delete next[taskId];
      sessionEditedTaskIds.delete(taskId);
      sessionEditedTaskFields.delete(taskId);
      set({ sessionPendingCreates: next, lastChangeTime: Date.now() }, false, `unmarkPendingCreate/${taskId}`);
    },

    markPendingDelete: (taskId: string) => {
      const state = get();
      const nextCreates = { ...state.sessionPendingCreates };
      delete nextCreates[taskId];
      const nextDeletes = new Set(state.sessionPendingDeletes);
      nextDeletes.add(taskId);
      const nextAffected = new Set(state.sessionAffectedTaskIds);
      nextAffected.delete(taskId);
      const nextEdited = new Set(state.sessionEditedTaskIds);
      nextEdited.delete(taskId);
      const nextFields = new Map(state.sessionEditedTaskFields);
      nextFields.delete(taskId);
      const now = Date.now();
      set({
        sessionPendingCreates: nextCreates,
        sessionPendingDeletes: nextDeletes,
        sessionAffectedTaskIds: nextAffected,
        sessionEditedTaskIds: nextEdited,
        sessionEditedTaskFields: nextFields,
        sessionStartTime: state.sessionStartTime || now,
        lastChangeTime: now,
      }, false, `markPendingDelete/${taskId}`);
    },

    unmarkPendingDelete: (taskId: string) => {
      const state = get();
      if (!state.sessionPendingDeletes.has(taskId)) return;
      const nextDeletes = new Set(state.sessionPendingDeletes);
      nextDeletes.delete(taskId);
      set({ sessionPendingDeletes: nextDeletes, lastChangeTime: Date.now() }, false, `unmarkPendingDelete/${taskId}`);
    },

    setLocationTree: (tree: LocationTreeNode[] | null) => {
      set({ locationTree: tree }, false, 'setLocationTree');
    },
      }),
      {
        name: `TaskStore-${projectId}`,
        enabled: true,
        serialize: {
          options: {
            // Convert Sets to arrays for Redux DevTools display
            replacer: (key: string, value: any) => {
              if (value instanceof Set) {
                return Array.from(value);
              }
              return value;
            }
          }
        }
      }
    )
  );
}

/**
 * Create Zustand Task Store that implements ITaskStore interface
 */
export function createZustandTaskStore(projectId: string): ITaskStore {
  const zustandStore = createZustandStore(projectId);

  // Patch listeners (not used with mutable updates)
  const patchListeners = new Set<PatchListener>();

  // Batch recording state (not used with mutable updates)
  let isRecording = false;

  // Cache for getDhtmlxTasks
  let cachedDhtmlxTasks: DhtmlxTask[] | null = null;
  let cachedTasksRevision: number = -1;
  let cachedDhtmlxIndex: Map<string, number> | null = null;

  let cachedSortedTasks: TaskSnapshot[] | null = null;
  let cachedSortedTasksRevision = -1;
  let cachedLinkList: LinkSnapshot[] | null = null;
  let cachedLinkListRevision = -1;

  const rebuildDhtmlxIndex = (rows: DhtmlxTask[]) => {
    const index = new Map<string, number>();
    for (let i = 0; i < rows.length; i++) {
      index.set(String(rows[i].id), i);
    }
    cachedDhtmlxIndex = index;
  };

  const invalidateDhtmlxCache = () => {
    cachedDhtmlxTasks = null;
    cachedTasksRevision = -1;
    cachedDhtmlxIndex = null;
    cachedSortedTasks = null;
    cachedSortedTasksRevision = -1;
  };

  const isStructuralTaskPatch = (changes: Partial<TaskSnapshot>): boolean =>
    changes.parentTaskId !== undefined ||
    changes.outlinePosition !== undefined ||
    changes.outlineLevel !== undefined ||
    changes.wbs !== undefined;

  const snapshotToDhtmlxTask = (
    task: TaskSnapshot,
    taskById: Map<string, TaskSnapshot>,
  ): DhtmlxTask => {
    const calendarId = task.calendarId != null ? String(task.calendarId).trim() : '';
    const schedule = buildDhtmlxScheduleFields({
      start: task.start,
      finish: task.finish,
      duration: task.duration,
      milestone: task.milestone,
    });
    return {
      id: task.id,
      text: resolveTaskName(task),
      parent: getDhtmlxParentId(task, taskById),
      start_date: schedule.start_date,
      end_date: schedule.end_date,
      duration: schedule.duration,
      actualStart: task.actualStart ?? null,
      actualFinish: task.actualFinish ?? null,
      actualDuration: task.actualDuration ?? null,
      progress: (task.percentComplete || 0) / 100,
      type: task.milestone ? 'milestone' : task.summary ? 'project' : 'task',
      open: true,
      taskType: task.taskType,
      locationType: task.locationType,
      locationId: task.locationId,
      ...(calendarId ? { calendar_id: calendarId } : {}),
      auto_scheduling: true,
      readonly: task.readonly,
    };
  };

  const findDhtmlxInsertIndex = (
    cached: DhtmlxTask[],
    newTask: TaskSnapshot,
    tasksMap: Record<string, TaskSnapshot>,
  ): number => {
    let lo = 0;
    let hi = cached.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const midSnap = tasksMap[String(cached[mid].id)];
      if (!midSnap || compareTaskSnapshots(newTask, midSnap) > 0) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }
    return lo;
  };

  const tryUpdateDhtmlxCacheAfterAdd = (
    added: TaskSnapshot[],
    tasksMap: Record<string, TaskSnapshot>,
    revisionBefore: number,
    revisionAfter: number,
  ): void => {
    if (cachedTasksRevision !== revisionBefore || !cachedDhtmlxTasks) {
      return;
    }
    const allTasks = Object.values(tasksMap);
    const taskById = new Map(allTasks.map((t) => [t.id, t]));
    const visibleAdded = added
      .filter((t) => !isPlanningShellTask(t, allTasks))
      .sort(compareTaskSnapshots);
    let next = cachedDhtmlxTasks;
    for (const snap of visibleAdded) {
      const row = snapshotToDhtmlxTask(snap, taskById);
      const idx = findDhtmlxInsertIndex(next, snap, tasksMap);
      const inserted = next.slice();
      inserted.splice(idx, 0, row);
      next = inserted;
    }
    cachedDhtmlxTasks = next;
    cachedTasksRevision = revisionAfter;
    rebuildDhtmlxIndex(next);
    cachedSortedTasks = null;
    cachedSortedTasksRevision = -1;
  };

  const tryPatchDhtmlxCacheAfterUpdate = (
    updates: Map<string, Partial<TaskSnapshot>>,
    tasksMap: Record<string, TaskSnapshot>,
    revisionBefore: number,
    revisionAfter: number,
  ): boolean => {
    // No size ceiling: schedule cascades can touch thousands of rows. In-place
    // patch keeps getDhtmlxTasks() array identity so ReactGantt does not deep-diff ~16k.
    if (
      cachedTasksRevision !== revisionBefore ||
      !cachedDhtmlxTasks ||
      updates.size === 0
    ) {
      return false;
    }
    if (!cachedDhtmlxIndex) {
      rebuildDhtmlxIndex(cachedDhtmlxTasks);
    }
    const index = cachedDhtmlxIndex;
    if (!index) return false;

    // Schedule-only patches: mutate cached rows in place (no full snapshot rebuild).
    const scheduleOnly = Array.from(updates.values()).every(
      (c) =>
        c.parentTaskId === undefined &&
        c.outlinePosition === undefined &&
        c.outlineLevel === undefined &&
        c.wbs === undefined &&
        (c.start !== undefined ||
          c.finish !== undefined ||
          c.duration !== undefined ||
          c.percentComplete !== undefined ||
          c.taskName !== undefined ||
          c.actualStart !== undefined ||
          c.actualFinish !== undefined ||
          c.actualDuration !== undefined ||
          c.notes !== undefined ||
          c.taskType !== undefined ||
          c.locationType !== undefined ||
          c.locationId !== undefined),
    );

    if (scheduleOnly) {
      for (const [id, changes] of updates) {
        const existing = tasksMap[id];
        const idx = index.get(String(id));
        // Skip rows absent from the visible cache (shells / filtered) — do not
        // invalidate the whole ~16k array identity over a single miss.
        if (idx == null || !existing || !cachedDhtmlxTasks[idx]) {
          continue;
        }
        const nextSnap = { ...existing, ...changes };
        const row = cachedDhtmlxTasks[idx];
        if (changes.start !== undefined || changes.finish !== undefined || changes.duration !== undefined) {
          const schedule = buildDhtmlxScheduleFields({
            start: nextSnap.start,
            finish: nextSnap.finish,
            duration: nextSnap.duration,
            milestone: nextSnap.milestone,
          });
          if (schedule.start_date) row.start_date = schedule.start_date;
          if (schedule.end_date) row.end_date = schedule.end_date;
          if (schedule.duration != null) row.duration = schedule.duration;
        }
        if (changes.percentComplete !== undefined) {
          row.progress = (nextSnap.percentComplete || 0) / 100;
        }
        if (changes.taskName !== undefined) {
          row.text = resolveTaskName(nextSnap);
        }
        if (changes.actualStart !== undefined) row.actualStart = nextSnap.actualStart ?? null;
        if (changes.actualFinish !== undefined) row.actualFinish = nextSnap.actualFinish ?? null;
        if (changes.actualDuration !== undefined) {
          row.actualDuration = nextSnap.actualDuration ?? null;
        }
        if (changes.taskType !== undefined) row.taskType = nextSnap.taskType;
        if (changes.locationType !== undefined) row.locationType = nextSnap.locationType;
        if (changes.locationId !== undefined) row.locationId = nextSnap.locationId;
      }
    } else {
      const taskById = new Map(Object.entries(tasksMap));
      for (const [id, changes] of updates) {
        const existing = tasksMap[id];
        const idx = index.get(String(id));
        if (idx == null || !existing) {
          continue;
        }
        const nextSnap = { ...existing, ...changes };
        taskById.set(id, nextSnap);
        cachedDhtmlxTasks[idx] = snapshotToDhtmlxTask(nextSnap, taskById);
      }
    }
    cachedTasksRevision = revisionAfter;
    // Never bump sorted-cache revision while keeping old TaskSnapshot refs — that made
    // getTasks() return stale rows (e.g. notes edits missing from Save-to-Server payload).
    if (cachedSortedTasks && cachedSortedTasksRevision === revisionBefore) {
      const byId = new Map(cachedSortedTasks.map((t, i) => [String(t.id), i]));
      let patchedAll = true;
      for (const [id, changes] of updates) {
        const sortedIdx = byId.get(String(id));
        if (sortedIdx == null) {
          patchedAll = false;
          break;
        }
        cachedSortedTasks[sortedIdx] = { ...cachedSortedTasks[sortedIdx], ...changes };
      }
      if (patchedAll) {
        cachedSortedTasksRevision = revisionAfter;
      } else {
        cachedSortedTasks = null;
        cachedSortedTasksRevision = -1;
      }
    } else {
      cachedSortedTasks = null;
      cachedSortedTasksRevision = -1;
    }
    return true;
  };

  const planningShellIds = (orderedTasks: TaskSnapshot[]): Set<string> => {
    const shellIds = new Set<string>();
    for (const t of orderedTasks) {
      if (Number(t.outlineLevel) === 1 && t.parentTaskId) {
        shellIds.add(String(t.parentTaskId));
      }
    }
    return shellIds;
  };

  // Cache parent ids for O(1) leaf checks (invalidated with tasksRevision)
  let cachedParentTaskIds: Set<string> | null = null;
  let cachedParentTaskIdsRevision: number = -1;

  const getParentTaskIds = (): Set<string> => {
    const { tasks, tasksRevision } = zustandStore.getState();
    if (cachedParentTaskIdsRevision === tasksRevision && cachedParentTaskIds) {
      return cachedParentTaskIds;
    }
    const parentIds = new Set<string>();
    for (const t of Object.values(tasks)) {
      if (t.parentTaskId) parentIds.add(String(t.parentTaskId));
    }
    cachedParentTaskIds = parentIds;
    cachedParentTaskIdsRevision = tasksRevision;
    return parentIds;
  };

  const getTaskValues = (): TaskSnapshot[] => {
    return Object.values(zustandStore.getState().tasks);
  };

  const getTasks = (): TaskSnapshot[] => {
    const { tasks, tasksRevision } = zustandStore.getState();
    if (cachedSortedTasksRevision === tasksRevision && cachedSortedTasks) {
      return cachedSortedTasks;
    }
    const sorted = Object.values(tasks).sort(compareTaskSnapshots);
    cachedSortedTasks = sorted;
    cachedSortedTasksRevision = tasksRevision;
    return sorted;
  };

  const getLinks = (): LinkSnapshot[] => {
    const { links, linksRevision } = zustandStore.getState();
    if (cachedLinkListRevision === linksRevision && cachedLinkList) {
      return cachedLinkList;
    }
    const list = Object.values(links);
    cachedLinkList = list;
    cachedLinkListRevision = linksRevision;
    return list;
  };

  // Computed: Get dhtmlx tasks (cached by revision; patched in place for field-only edits)
  const getDhtmlxTasks = (): DhtmlxTask[] => {
    const { tasks, tasksRevision } = zustandStore.getState();

    // Return cached result if revision hasn't changed
    if (cachedTasksRevision === tasksRevision && cachedDhtmlxTasks) {
      return cachedDhtmlxTasks;
    }

    const result: DhtmlxTask[] = [];

    const orderedTasks = Object.values(tasks).sort(compareTaskSnapshots);
    const taskById = new Map(orderedTasks.map((t) => [t.id, t]));
    const shellIds = planningShellIds(orderedTasks);
    for (const task of orderedTasks) {
      if (Number(task.outlineLevel) === 0 && shellIds.has(String(task.id))) {
        continue;
      }
      result.push(snapshotToDhtmlxTask(task, taskById));
    }

    cachedTasksRevision = tasksRevision;
    cachedDhtmlxTasks = result;
    rebuildDhtmlxIndex(result);
    cachedSortedTasks = orderedTasks;
    cachedSortedTasksRevision = tasksRevision;

    return result;
  };

  // Build location hierarchy
  const buildLocationHierarchy = (): LocationHierarchyPayload[] => {
    const { tasks } = zustandStore.getState();
    const result: LocationHierarchyPayload[] = [];
    const memoizedInfo = new Map<string, { level: number; parentId: string | null }>();

    function getLocationInfo(taskId: string): { level: number; parentId: string | null } {
      if (memoizedInfo.has(taskId)) {
        return memoizedInfo.get(taskId)!;
      }

      const task = tasks[taskId];
      if (!task) {
        return { level: 1, parentId: null };
      }

      if (!task.parentTaskId) {
        const info = { level: 1, parentId: null };
        memoizedInfo.set(taskId, info);
        return info;
      }

      const parent = tasks[task.parentTaskId];
      if (!parent) {
        const info = { level: 1, parentId: null };
        memoizedInfo.set(taskId, info);
        return info;
      }

      if (parent.taskType === 'Location') {
        const parentInfo = getLocationInfo(parent.id);
        const info = {
          level: parentInfo.level + 1,
          parentId: parent.id,
        };
        memoizedInfo.set(taskId, info);
        return info;
      }

      const grandparentInfo = parent.parentTaskId
        ? getLocationInfo(parent.parentTaskId)
        : { level: 0, parentId: null };

      const info = {
        level: grandparentInfo.level + 1,
        parentId: grandparentInfo.parentId,
      };
      memoizedInfo.set(taskId, info);
      return info;
    }

    for (const task of Object.values(tasks)) {
      if (task.taskType === 'Location') {
        const info = getLocationInfo(task.id);
        result.push({
          taskId: task.id,
          locationType: task.locationType,
          locationLevel: info.level,
          parentLocationTaskId: info.parentId,
        });
      }
    }

    return result;
  };

  // Return ITaskStore implementation
  const taskStore: ITaskStore = {
    // State Access
    getTasks,
    getTaskValues,
    getTask: (id) => zustandStore.getState().tasks[id],
    getTasksRevision: () => zustandStore.getState().tasksRevision,
    getLinksRevision: () => zustandStore.getState().linksRevision,
    taskHasChildren: (taskId) => getParentTaskIds().has(String(taskId)),
    getLinks,
    getDhtmlxTasks,
    getDhtmlxLinks: () => getLinks(),

    // Mutations (no patch tracking for performance)
    updateTask: (id, changes) => {
      const revisionBefore = zustandStore.getState().tasksRevision;
      const { tasks } = zustandStore.getState();
      const patched = !isStructuralTaskPatch(changes)
        && tryPatchDhtmlxCacheAfterUpdate(new Map([[id, changes]]), tasks, revisionBefore, revisionBefore + 1);
      zustandStore.getState().updateTask(id, changes);
      if (!patched) {
        invalidateDhtmlxCache();
      }
    },

    batchUpdateTasks: (updates) => {
      if (updates.size === 0) return;
      const revisionBefore = zustandStore.getState().tasksRevision;
      const { tasks } = zustandStore.getState();
      const structural = Array.from(updates.values()).some(isStructuralTaskPatch);
      const patched =
        !structural &&
        tryPatchDhtmlxCacheAfterUpdate(updates, tasks, revisionBefore, revisionBefore + 1);
      zustandStore.getState().batchUpdateTasks(updates);
      if (!patched) {
        invalidateDhtmlxCache();
      }
    },

    addTask: (task) => {
      const revisionBefore = zustandStore.getState().tasksRevision;
      zustandStore.getState().addTask(task);
      const { tasks, tasksRevision } = zustandStore.getState();
      tryUpdateDhtmlxCacheAfterAdd([task], tasks, revisionBefore, tasksRevision);
    },

    addTasks: (incoming) => {
      if (incoming.length === 0) return;
      const revisionBefore = zustandStore.getState().tasksRevision;
      zustandStore.getState().addTasks(incoming);
      const { tasks, tasksRevision } = zustandStore.getState();
      tryUpdateDhtmlxCacheAfterAdd(incoming, tasks, revisionBefore, tasksRevision);
    },

    removeTask: (id) => {
      invalidateDhtmlxCache();
      zustandStore.getState().removeTask(id);
    },
    addLink: (link) => {
      cachedLinkList = null;
      cachedLinkListRevision = -1;
      zustandStore.getState().addLink(link);
    },
    removeLink: (id) => {
      cachedLinkList = null;
      cachedLinkListRevision = -1;
      zustandStore.getState().removeLink(id);
    },
    setTasks: (tasks) => {
      invalidateDhtmlxCache();
      zustandStore.getState().setTasks(tasks);
    },
    setLinks: (links) => {
      cachedLinkList = null;
      cachedLinkListRevision = -1;
      zustandStore.getState().setLinks(links);
    },

    getLocationTree: () => zustandStore.getState().locationTree ?? null,
    setLocationTree: (tree) => zustandStore.getState().setLocationTree(tree),

    // Batch Recording (no-op for mutable store)
    startBatchRecording: () => {
      isRecording = true;
    },

    stopBatchRecording: () => {
      isRecording = false;
      return [];
    },

    isBatchRecording: () => isRecording,

    // Subscriptions
    subscribe: (listener) => {
      return zustandStore.subscribe(listener);
    },

    onPatch: (listener) => {
      patchListeners.add(listener);
      return () => patchListeners.delete(listener);
    },

    // Persistence
    hydrate: async (projectId) => {
      zustandStore.getState().setLoading(true);
      zustandStore.getState().setProjectId(projectId);

      try {
        const data = await loadTasksFromDB(projectId);
        const locationTree = await loadLocationTreeFromDB(projectId);

        if (data) {
          zustandStore.getState().setTasks(data.tasks);
          zustandStore.getState().setLinks(data.links);
          if (locationTree) {
            zustandStore.getState().setLocationTree(locationTree);
          }
          zustandStore.getState().setLoading(false);
          return true;
        }

        if (locationTree) {
          zustandStore.getState().setLocationTree(locationTree);
        }
        zustandStore.getState().setLoading(false);
        return false;
      } catch (error: any) {
        zustandStore.getState().setError(error?.message || 'Failed to hydrate store');
        zustandStore.getState().setLoading(false);
        return false;
      }
    },

    persist: async (projectId) => {
      try {
        const state = zustandStore.getState();
        await saveTasksToDB(
          projectId,
          Object.values(state.tasks),
          Object.values(state.links)
        );
      } catch (error: any) {
        zustandStore.getState().setError(error?.message || 'Failed to persist store');
        throw error;
      }
    },

    persistTaskPatches: async (taskIds) => {
      if (taskIds.length === 0) return;
      const { tasks } = zustandStore.getState();
      const rows: TaskSnapshot[] = [];
      for (const id of taskIds) {
        const row = tasks[id];
        if (row) rows.push(row);
      }
      if (rows.length === 0) return;
      const CHUNK = 400;
      try {
        for (let i = 0; i < rows.length; i += CHUNK) {
          const slice = rows.slice(i, i + CHUNK);
          await putTasksInDB(slice);
          // Yield between chunks so large cascades do not block the UI thread.
          if (i + CHUNK < rows.length) {
            await new Promise<void>((resolve) => {
              if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => resolve());
              } else {
                setTimeout(resolve, 0);
              }
            });
          }
        }
      } catch (error: any) {
        zustandStore.getState().setError(error?.message || 'Failed to persist task patches');
        throw error;
      }
    },

    // Computed Views
    buildLocationHierarchy,

    // UI State
    getSelectedTaskId: () => zustandStore.getState().selectedTaskId,
    setSelectedTaskId: (id) => zustandStore.getState().setSelectedTaskId(id),

    // Auto-Scheduling State
    getAutoSchedulingEnabled: () => zustandStore.getState().autoSchedulingEnabled,
    setAutoSchedulingEnabled: (enabled) => zustandStore.getState().setAutoSchedulingEnabled(enabled),

    // Session Change Tracking
    addAffectedTasks: (ids) => zustandStore.getState().addAffectedTasks(ids),
    addEditedTasks: (ids, fields) => zustandStore.getState().addEditedTasks(ids, fields),
    addAffectedLinks: (ids) => zustandStore.getState().addAffectedLinks(ids),
    clearSessionChanges: () => zustandStore.getState().clearSessionChanges(),
    exportSessionSnapshot: () => zustandStore.getState().exportSessionSnapshot(),
    restoreSessionSnapshot: (snapshot) =>
      zustandStore.getState().restoreSessionSnapshot(snapshot),
    markPendingCreate: (taskId, payload) =>
      zustandStore.getState().markPendingCreate(taskId, payload),
    unmarkPendingCreate: (taskId) => zustandStore.getState().unmarkPendingCreate(taskId),
    isPendingCreate: (taskId) => Boolean(zustandStore.getState().sessionPendingCreates[taskId]),
    getPendingCreates: () => ({ ...zustandStore.getState().sessionPendingCreates }),
    markPendingDelete: (taskId) => zustandStore.getState().markPendingDelete(taskId),
    unmarkPendingDelete: (taskId) => zustandStore.getState().unmarkPendingDelete(taskId),
    isPendingDelete: (taskId) => zustandStore.getState().sessionPendingDeletes.has(taskId),
    getPendingDeleteIds: () => Array.from(zustandStore.getState().sessionPendingDeletes),
    getPendingProjectCreate: () => zustandStore.getState().sessionPendingProjectCreate,
    setPendingProjectCreate: (payload) =>
      zustandStore.getState().setPendingProjectCreate(payload),
    getAffectedTaskIds: () => Array.from(zustandStore.getState().sessionAffectedTaskIds),
    getEditedTaskIds: () => Array.from(zustandStore.getState().sessionEditedTaskIds),
    getEditedTaskFields: (taskId) => {
      const set = zustandStore.getState().sessionEditedTaskFields.get(taskId);
      return set ? Array.from(set) : [];
    },
    getEditedTaskFieldsMap: () => {
      const out: Record<string, string[]> = {};
      for (const [id, set] of zustandStore.getState().sessionEditedTaskFields) {
        out[id] = Array.from(set);
      }
      return out;
    },
    getAffectedLinkIds: () => {
      const state = zustandStore.getState();
      return [
        ...state.sessionAffectedRemovedLinkIds,
        ...state.sessionAffectedAddedLinkIds,
      ];
    },
    getPendingDependencyCreates: () =>
      Object.values(zustandStore.getState().sessionPendingDependencyCreates),
    getPendingDependencyDeletes: () =>
      Array.from(zustandStore.getState().sessionPendingDependencyDeletes),
    trackAffectedLinkChanges: (removed, added) =>
      zustandStore.getState().trackAffectedLinkChanges(removed, added),
    getSessionMetadata: () => {
      const state = zustandStore.getState();
      const pendingCreateCount = Object.keys(state.sessionPendingCreates).length;
      const pendingDeleteCount = state.sessionPendingDeletes.size;
      const pendingProject = state.sessionPendingProjectCreate ? 1 : 0;
      const pendingDepCreateCount = Object.keys(state.sessionPendingDependencyCreates).length;
      const pendingDepDeleteCount = state.sessionPendingDependencyDeletes.size;
      return {
        startTime: state.sessionStartTime,
        lastChange: state.lastChangeTime,
        taskCount:
          state.sessionAffectedTaskIds.size +
          pendingCreateCount +
          pendingDeleteCount +
          pendingProject,
        linkCount: Math.max(
          pendingDepCreateCount + pendingDepDeleteCount,
          state.sessionAffectedRemovedLinkIds.size + state.sessionAffectedAddedLinkIds.size,
        ),
        pendingCreateCount,
        pendingDeleteCount,
        pendingProjectCreate: pendingProject > 0,
      };
    },

    // Loading State
    isLoading: () => zustandStore.getState().loading,
    getError: () => zustandStore.getState().error,
    setLoading: (loading) => zustandStore.getState().setLoading(loading),
    setError: (error) => zustandStore.getState().setError(error),

    // Metadata
    getStoreType: () => 'zustand',
    getMetrics: () => ({}),
  };

  return taskStore;
}
